import {
  PermissionsProvider,
  toPermissionKeys,
  unknownPermissionKeys,
} from '@smartsash/permissions';
import { useEffect, useMemo, type ReactNode } from 'react';

import { useSessionPermissions } from '@/services/auth';
import { logger } from '@/shared/logger';

/**
 * Keys already reported, so a re-render or a session refresh does not repeat the warning.
 * Module scope rather than a ref: the point is one message per key per page load.
 */
const reportedUnknownKeys = new Set<string>();

/**
 * Warn when the session carries permissions the catalogue does not define.
 *
 * Dropping unknown keys is the safe behaviour, but it is also invisible: an API serving a
 * different vocabulary (the pre-rename `Tenant.*`, say) would hide every gated control with no
 * explanation. Until the CI drift check has a backend export to compare against, this is the
 * only signal that the mismatch exists. Development only — `import.meta.env.DEV` is a
 * build-time literal, so the whole branch is eliminated from production bundles.
 */
function reportCatalogueDrift(sessionPermissions: readonly string[]): void {
  const unknown = unknownPermissionKeys(sessionPermissions).filter(
    (key) => !reportedUnknownKeys.has(key),
  );
  if (unknown.length === 0) return;

  for (const key of unknown) reportedUnknownKeys.add(key);
  logger.warn(
    `The session carries ${String(unknown.length)} permission key(s) missing from @smartsash/permissions: ` +
      `${unknown.join(', ')}. They grant nothing until the matrix is resynced (HS-866).`,
  );
}

/** Feeds the session's permissions into `@smartsash/permissions` for the whole app (HS-866). */
export function SessionPermissionsProvider({ children }: { children: ReactNode }) {
  const sessionPermissions = useSessionPermissions();
  const permissions = useMemo(() => toPermissionKeys(sessionPermissions), [sessionPermissions]);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    reportCatalogueDrift(sessionPermissions);
  }, [sessionPermissions]);

  return <PermissionsProvider permissions={permissions}>{children}</PermissionsProvider>;
}
